import { useListBox } from '@react-aria/listbox';
import { mergeProps } from '@react-aria/utils';
import useMergedRef from '@react-hook/merged-ref';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTheme } from '../../hooks/index.js';
import { useScrollToLastChild } from '../../hooks/use-scroll-to-target-child/index.js';
import { isCtrlKeyPressed, omit } from '../../utils/index.js';
import { Label } from '../label/index.js';
import { isSection } from '../select/utils.js';
import { VisuallyHidden } from '../visually-hidden/index.js';
import { ListBoxItem } from './list-box-item.component.js';
import { ListBoxLoadingSkeleton } from './list-box-loading-skeleton.component.js';
import { ListBoxNoneItem } from './list-box-none-item.component.js';
import { ListBoxSection } from './list-box-section.component.js';
import { ListBoxElement, ListBoxEmptyState, ListBoxEmptyStateWrapper, } from './list-box.styles.js';
import { getLastItem } from './utils.js';
const DUMMY_SKELETON_OPTIONS_LENGTH = 3;
export const ListBox = React.forwardRef(({ state, dataAutomation = 'listbox', label, emptyState, showSelectionCheck = true, ...props }, forwardedRef) => {
    const listRef = useRef(null);
    const theme = useTheme();
    const lastItem = getLastItem(props.options);
    const onScrollRevealLastItem = useCallback(() => lastItem &&
        lastItem.value &&
        props.onScrollRevealLastItem?.(lastItem.value.id), [lastItem, props.onScrollRevealLastItem]);
    const { setRootRef } = useScrollToLastChild({
        callback: onScrollRevealLastItem,
        callbackThrottle: theme.throttleTime.standard,
        childItems: props.options,
    });
    const ref = useMergedRef(forwardedRef, listRef, setRootRef);
    const { listBoxProps, labelProps } = useListBox({
        label,
        shouldSelectOnPressUp: true,
        ...props,
    }, state, listRef);
    const onKeyDown = useCallback((e) => {
        if (e.key !== 'Escape' &&
            !(e.key === 'a' && isCtrlKeyPressed(e.nativeEvent))) {
            listBoxProps.onKeyDown?.(e);
        }
        else {
            props.onKeyDown?.(e);
        }
    }, [listBoxProps.onKeyDown, props.onKeyDown]);
    const renderListBoxItemOrSection = (i) => {
        if (!isSection(i.value) && i.value?.variant === 'none') {
            return (React.createElement(ListBoxNoneItem, { key: i.key, listItem: i, state: state, dataAutomation: `${dataAutomation}/${i.key}`, showSelectionCheck: showSelectionCheck }));
        }
        if (props.loading)
            return React.createElement(ListBoxLoadingSkeleton, { key: i.key });
        if (isSection(i.value)) {
            const isUsingCheckbox = i.value.items.some(({ variant }) => variant === 'checkbox-item');
            return (React.createElement(ListBoxSection, { key: i.value.id, isUsingCheckbox: isUsingCheckbox, title: i.value.label }, [...i.childNodes].map((node) => renderListBoxItemOrSection(node))));
        }
        return (React.createElement(ListBoxItem, { key: i.key, listItem: i, state: state, dataAutomation: `${dataAutomation}/${i.key}`, showSelectionCheck: showSelectionCheck }));
    };
    const hasOneOfVariants = useCallback((item, variants) => variants.includes(item.variant ?? 'item'), []);
    const hasOptionWithOneOfVariants = useCallback((variants) => props.options.some((option) => {
        if (isSection(option.value)) {
            const existsInSectionItems = [...option.childNodes].some((nestedOption) => hasOneOfVariants(nestedOption.value, variants));
            return existsInSectionItems;
        }
        return option.value && hasOneOfVariants(option.value, variants);
    }), [props.options]);
    const shouldShowDummySkeleton = useMemo(() => props.loading && !hasOptionWithOneOfVariants(['item', 'checkbox-item']), [props.loading, hasOptionWithOneOfVariants]);
    const shouldShowEmptyState = useMemo(() => !props.loading &&
        !hasOptionWithOneOfVariants(['none', 'item', 'checkbox-item']), [props.loading, hasOptionWithOneOfVariants]);
    return (React.createElement(React.Fragment, null,
        label ? (React.createElement(VisuallyHidden, null,
            React.createElement(Label, { ...omit(labelProps, 'color'), elementType: 'span' }, label))) : null,
        React.createElement(ListBoxElement, { ref: ref, ...mergeProps(omit(props, 'onSelectionChange'), omit(listBoxProps, 'onKeyDown')), onKeyDown: onKeyDown, "data-automation": dataAutomation },
            props.options.map(renderListBoxItemOrSection),
            shouldShowEmptyState && (React.createElement(ListBoxEmptyStateWrapper, null,
                React.createElement(ListBoxEmptyState, { role: 'status', ...emptyState }))),
            shouldShowDummySkeleton
                ? Array(DUMMY_SKELETON_OPTIONS_LENGTH)
                    .fill(null)
                    .map((_, i) => React.createElement(ListBoxLoadingSkeleton, { key: i }))
                : null)));
});
//# sourceMappingURL=list-box.component.js.map